import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  parseDeepLink,
  parseDateParams,
} from "../../../components/Search/Jvvnl/deepLink";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { CriticalPeriodsToggle } from "../../../components/CriticalPeriodsToggle";
import { R_CHART_TYPES } from "../../Rajasthan/const";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import DownloadButton from "../../../components/DownloadButton";
import { useDispatch } from "react-redux";
import { updateDefaultDates } from "../../../store/state/DefaultDatesSetter";
import { setViewMode } from "../../../store/state/ViewMode";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import MetricChip from "../../../components/Metrics";
import { FilterTab } from "../../../components/Filters";
import { DataTabs } from "../../Rajasthan/Demand/Data";
import {
  JVVNL_DEMAND_UNITS,
  JVVNL_AGG_UNITS,
  JVVNL_GEN_UNITS,
  FUNCTIONS,
  WEATHER_PARAMS,
  Intervals,
  GROUP_BY_OPTIONS,
} from "./feederChart/constants";
import { useJvvnlFeederChartData } from "./feederChart/useJvvnlFeederChartData";
import { JvvnlEntitySearchBar } from "./feederChart/JvvnlEntitySearchBar";

export type { EmbeddedNet } from "./feederChart/types";

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================
// Shared constants/helpers live in feederChart/constants.ts (used by both the
// engine hook and this render shell). VARIABLES is render-only, kept local.
const VARIABLES = ["Actual", "Weather", "Forecast"] as const;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// Standalone JVVNL feeder-demand page. The popup/embedded variant lives in
// feederChart/FeederChartPopup; this shell is always standalone.
export function JvvnlFeederDemand() {
  const { state, setters, derived, internal } = useJvvnlFeederChartData({
    kind: "standalone",
  });

  const {
    selectedUnit,
    selectedGroupBy,
    selectedBlock,
    selectedChartType,
    selectedInterval,
    criticalPeriodKinds,
    selectedFunction,
    selectedVariables,
    feederOpenSignal,
    selectedFeederOptions,
    selectedSubstationOptions,
    selectedFeederSubstations,
    selectedCircle,
    selectedFeederTransco,
    netCircle,
    netTransco,
    netDiscom,
    discomSubstations,
    selectedTranscoForDiscom,
    selectedDiscomSubstations,
    netMetrics,
    genMetrics,
    allPlants,
    selectedPlantTypeOptions,
    selectedPlantOptions,
    selectedGenCircle,
    chartData,
    loading,
    circleOptions,
  } = state;
  const {
    setSelectedUnit,
    setSelectedGroupBy,
    setSelectedBlock,
    setSelectedChartType,
    setSelectedInterval,
    setCriticalPeriodKinds,
    setSelectedFunction,
    setSelectedVariables,
    setSelectedWeatherParams,
    setSelectedFeederOptions,
    setSelectedSubstationOptions,
    setSelectedFeederSubstations,
    setSelectedCircle,
    setSelectedFeederTransco,
    setNetCircle,
    setNetTransco,
    setNetDiscom,
    setSelectedTranscoForDiscom,
    setSelectedDiscomSubstations,
    setSelectedPlantTypeOptions,
    setSelectedPlantOptions,
    setSelectedGenCircle,
    setCpActive,
  } = setters;
  const {
    selectedWeatherParam,
    selectedWeatherMetricLabel,
    legends,
    handleLegendSelect,
    selectedLegends,
    xDataKey,
    secondDataKey,
    blockDurationMinutes,
    hierSepClass,
    showGenerationSearch,
    showNetConsumptionSearch,
    showFeederSearch,
    showCircleSearch,
    showDiscomSubstationSearch,
    showSubstationSearch,
    tablePar,
    feederPeakLoadKw,
    feederAvgLoadKw,
    feederPeakTemp,
    feederAvgTemp,
    groupPeakLoad,
    groupAvgLoad,
    groupPeakTemp,
    groupAvgTemp,
    groupLoadUnit,
    groupLabelPrefix,
    generationOnlyMode,
    netIsKw,
    dataTab,
    plantOptions,
    plantTypeOptions,
    substationOptions,
    feederSubstationOptions,
    netCircleOptions,
    netTranscoOptions,
    netDiscomOptions,
    transcoOptionsForDiscom,
    filteredDiscomSubstationOptions,
    filteredFeederOptions,
    cpActive,
  } = derived;
  const {
    effectiveTabKey,
    DefaultDates,
    feeders,
    feedersLoaded,
    substations,
    substationsLoaded,
    plantsLoaded,
    discomSubstationsLoaded,
    appliedDeepLinkRef,
    didInitPlantDefaultsRef,
    didInitDiscomSubDefaultsRef,
    didInitFeederDefaultsRef,
  } = internal;

  // ── Deep-link from global search ──
  // When navigated here with ?gb=feeder|plant&eid=...&elabel=..., switch to the
  // matching group-by and pre-select the entity once its metadata has loaded.
  // Standalone only (embedded popups manage their own selection). Applied once
  // per location.search so the user can freely change filters afterward.
  const location = useLocation();
  const dispatch = useDispatch();

  const switchedDeepLinkRef = useRef<string | null>(null);
  useEffect(() => {
    const link = parseDeepLink(location.search);
    if (!link) return;
    if (switchedDeepLinkRef.current === location.search) return;
    const rowKey =
      link.groupBy === "feeder"
        ? "group_feeder"
        : link.groupBy === "plant"
          ? "group_generation"
          : link.groupBy === "discom_substation"
            ? "group_discom_substation"
            : link.groupBy === "circle"
              ? "group_discom"
              : "group_substation";
    const target = GROUP_BY_OPTIONS.find((o) => o.rowKey === rowKey);
    if (target) setSelectedGroupBy(target);
    dispatch(setViewMode({ label: "Chart", value: "chart" }));
    if (link.groupBy === "plant") {
      // Block the auto-default plant fill so it can't clobber our selection.
      didInitPlantDefaultsRef.current = true;
    }
    if (link.groupBy === "discom_substation") {
      // Block the default transco+discom fill so it can't clobber our selection.
      didInitDiscomSubDefaultsRef.current = true;
    }
    switchedDeepLinkRef.current = location.search;
  }, [
    dispatch,
    location.search,
    setSelectedGroupBy,
    didInitPlantDefaultsRef,
    didInitDiscomSubDefaultsRef,
  ]);

  // Step 2: once the metadata for the deep-linked entity has loaded, apply the
  // selection. Re-runs as feeders/plants/substations arrive.
  useEffect(() => {
    const link = parseDeepLink(location.search);
    if (!link) return;
    if (appliedDeepLinkRef.current === location.search) return;

    if (link.groupBy === "feeder") {
      if (!feedersLoaded) return; // wait for feeders, re-run when loaded
      const match = feeders.find((f) => f.id === link.entityId);
      if (!match) {
        appliedDeepLinkRef.current = location.search;
        return;
      }
      const target = GROUP_BY_OPTIONS.find((o) => o.rowKey === "group_feeder");
      if (target) setSelectedGroupBy(target);
      // Block the default first-substation fill so it can't clobber us, and set
      // the feeder's OWN parent substation so the alignment effect (which resets
      // the feeder list to its substation's feeders) keeps our selection valid.
      didInitFeederDefaultsRef.current = true;
      const parentSub = match.discom_substation_name;
      if (parentSub) {
        setSelectedFeederSubstations([{ value: parentSub, label: parentSub }]);
      }
      setSelectedFeederOptions([{ value: match.id, label: match.name }]);
      appliedDeepLinkRef.current = location.search;
    } else if (link.groupBy === "plant") {
      if (!plantsLoaded) return; // wait for plants, re-run when loaded
      const match = allPlants.find((p) => p.id === link.entityId);
      if (!match) {
        appliedDeepLinkRef.current = location.search;
        return;
      }
      const target = GROUP_BY_OPTIONS.find(
        (o) => o.rowKey === "group_generation",
      );
      if (target) setSelectedGroupBy(target);
      // Block the auto-default plant fill so it doesn't clobber our selection.
      didInitPlantDefaultsRef.current = true;
      setSelectedPlantTypeOptions([
        { value: match.plant_type, label: match.plant_type },
      ]);
      setSelectedPlantOptions([{ value: match.id, label: match.name }]);
      appliedDeepLinkRef.current = location.search;
    } else if (link.groupBy === "substation") {
      if (!substationsLoaded) return; // wait for substations
      const match = substations.find(
        (s) => String(s.id) === String(link.entityId),
      );
      if (!match) {
        appliedDeepLinkRef.current = location.search;
        return;
      }
      const target = GROUP_BY_OPTIONS.find(
        (o) => o.rowKey === "group_substation",
      );
      if (target) setSelectedGroupBy(target);
      setSelectedSubstationOptions([{ value: match.id, label: match.station }]);
      appliedDeepLinkRef.current = location.search;
    } else if (link.groupBy === "discom_substation") {
      // Wait on the data itself, not the loaded flag: the fetch effect can early
      // -return (data already present) without ever flipping the flag, so the
      // flag is unreliable here while discomSubstations is populated.
      if (discomSubstations.length === 0) return;
      // Coerce both sides — the discom-substation API may return a string id
      // while the deep-link carries a parsed number.
      const match = discomSubstations.find(
        (d) => String(d.discom_substation_id) === String(link.entityId),
      );
      if (!match) {
        appliedDeepLinkRef.current = location.search;
        return;
      }
      const target = GROUP_BY_OPTIONS.find(
        (o) => o.rowKey === "group_discom_substation",
      );
      if (target) setSelectedGroupBy(target);
      // Block the default fill, then set the parent transco (CIRCLE → TRANSCO →
      // DISCOM SUB chain) so the transco-alignment effect keeps our discom valid.
      didInitDiscomSubDefaultsRef.current = true;
      if (match.transco_substation_id != null) {
        setSelectedTranscoForDiscom([
          {
            value: match.transco_substation_id,
            label: match.transco_substation_name ?? "",
          },
        ]);
      }
      setSelectedDiscomSubstations([
        {
          value: match.discom_substation_id,
          label: match.discom_substation_name,
        },
      ]);
      appliedDeepLinkRef.current = location.search;
    } else if (link.groupBy === "circle") {
      // Circle view = group_discom. Switch group-by, then pre-select the deep-
      // linked circle once the circle options have loaded. eid is the numeric
      // circle_id (string-coerced match — option values may be number).
      const target = GROUP_BY_OPTIONS.find((o) => o.rowKey === "group_discom");
      if (target) setSelectedGroupBy(target);
      if (circleOptions.length === 0) return; // wait for circle list
      const match = circleOptions.find(
        (o: any) => String(o.value) === String(link.entityId),
      );
      if (match) setSelectedCircle(match);
      appliedDeepLinkRef.current = location.search;
    }
  }, [
    location.search,
    feedersLoaded,
    feeders,
    plantsLoaded,
    allPlants,
    substationsLoaded,
    substations,
    discomSubstationsLoaded,
    discomSubstations,
    appliedDeepLinkRef,
    didInitPlantDefaultsRef,
    didInitDiscomSubDefaultsRef,
    didInitFeederDefaultsRef,
    setSelectedGroupBy,
    setSelectedPlantOptions,
    setSelectedPlantTypeOptions,
    setSelectedSubstationOptions,
    setSelectedFeederOptions,
    setSelectedFeederSubstations,
    setSelectedTranscoForDiscom,
    setSelectedDiscomSubstations,
    circleOptions,
    setSelectedCircle,
  ]);

  // Variable deep-links: when navigated with ?wparam=<key> (enable Weather + that
  // param) and/or ?fc=1 (enable Forecast) — from the global search "<entity>
  // temperature" / "<entity> forecast" or a bare weather/forecast result. Applied
  // once per location.search, standalone only.
  const appliedWparamRef = useRef<string | null>(null);
  useEffect(() => {
    if (appliedWparamRef.current === location.search) return;
    const link = parseDeepLink(location.search);
    const params = new URLSearchParams(location.search);
    const wparam = link?.weatherParam ?? params.get("wparam") ?? undefined;
    const forecast = link?.forecast || params.get("fc") === "1";
    if (!wparam && !forecast) {
      appliedWparamRef.current = location.search;
      return;
    }
    if (wparam) setSelectedWeatherParams([wparam]);
    setSelectedVariables((prev) => {
      let next = prev;
      if (wparam && !next.includes("Weather")) next = [...next, "Weather"];
      if (forecast && !next.includes("Forecast")) next = [...next, "Forecast"];
      return next;
    });
    appliedWparamRef.current = location.search;
  }, [location.search, setSelectedVariables, setSelectedWeatherParams]);

  // Apply a search-deep-linked date into the shared date slice once per
  // location.search, standalone only. The page reads its range from
  // defaultDates[effectiveTabKey].defaultRangeDate, so dispatching there is the
  // same contract the date picker uses.
  const appliedDateRef = useRef<string | null>(null);
  useEffect(() => {
    if (appliedDateRef.current === location.search) return;
    const dm = parseDateParams(location.search);
    if (!dm) {
      appliedDateRef.current = location.search;
      return;
    }
    // The slice indexes into defaultDates[tab] — only dispatch once it's seeded.
    if (!DefaultDates || !DefaultDates[effectiveTabKey]) return;
    const startStr = dm.date ?? dm.startDate!;
    const endStr = dm.date ?? dm.endDate!;
    const startDate = new Date(`${startStr}T00:00:00`);
    const endDate = new Date(`${endStr}T00:00:00`);
    dispatch(
      updateDefaultDates({
        activeTab: effectiveTabKey,
        dates: { startDate, endDate },
      }),
    );
    appliedDateRef.current = location.search;
  }, [location.search, DefaultDates, effectiveTabKey, dispatch]);

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((it) => it.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(_, i) => setSelectedInterval(Intervals[i])}
          />
          <FilterMenu
            title="Unit"
            items={
              generationOnlyMode
                ? [...JVVNL_GEN_UNITS]
                : selectedGroupBy.rowKey === "group_net_consumption"
                  ? netIsKw
                    ? ["KW", "KWh"]
                    : ["MW", "MWh"]
                  : selectedGroupBy.rowKey === "group_feeder" ||
                      selectedGroupBy.rowKey === "group_discom_substation"
                    ? [...JVVNL_DEMAND_UNITS]
                    : [...JVVNL_AGG_UNITS]
            }
            selectedItems={[selectedUnit]}
            onSelect={setSelectedUnit}
          />
          <FilterMenu
            title="Function"
            items={[...FUNCTIONS]}
            selectedItems={[selectedFunction]}
            onSelect={(_, i) => setSelectedFunction(FUNCTIONS[i])}
          />
          <FilterMenu
            title="Group By"
            sortAlphabetically={false}
            items={GROUP_BY_OPTIONS.map((i) => i.label)}
            selectedItems={[selectedGroupBy.label]}
            onSelect={(_, i) => {
              const next = GROUP_BY_OPTIONS[i];
              setSelectedGroupBy(next);
              if (next.rowKey === "group_generation") {
                setSelectedVariables((prev) =>
                  prev.filter((v) => v !== "Forecast"),
                );
              }
            }}
          />
          <FilterMenu
            title="Variable"
            items={
              selectedGroupBy.rowKey === "group_generation"
                ? VARIABLES.filter((v) => v !== "Forecast")
                : [...VARIABLES]
            }
            selectedItems={selectedVariables}
            setSelectedItems={(next: string[]) => setSelectedVariables(next)}
          />
          <FilterMenu
            title="Weather Metric"
            items={WEATHER_PARAMS.map((w) => ({
              value: w.key,
              label: w.label,
            }))}
            selectedItems={[selectedWeatherParam]}
            onSelect={(i) => setSelectedWeatherParams([i])}
            disabled={!selectedVariables.includes("Weather")}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((it) => it.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, i) => setSelectedChartType(R_CHART_TYPES[i])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer className="min-h-0">
        <div className="flex flex-wrap gap-4 my-0 justify-center">
          {selectedGroupBy.rowKey === "group_net_consumption" ? (
            <>
              <MetricChip
                items={[
                  {
                    label: "Peak Load",
                    value:
                      netMetrics?.peak_load_kw != null
                        ? Math.round((netMetrics.peak_load_kw / 1000) * 100) /
                          100
                        : undefined,
                    unit: "MW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Load",
                    value:
                      netMetrics?.avg_load_kw != null
                        ? Math.round((netMetrics.avg_load_kw / 1000) * 100) /
                          100
                        : undefined,
                    unit: "MW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Peak Temp",
                    value:
                      netMetrics?.peak_temp_c != null
                        ? Math.round(netMetrics.peak_temp_c * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Temp",
                    value:
                      netMetrics?.avg_temp_c != null
                        ? Math.round(netMetrics.avg_temp_c * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
            </>
          ) : selectedGroupBy.rowKey === "group_feeder" ? (
            <>
              <MetricChip
                items={[
                  {
                    label: "Peak Load",
                    value:
                      feederPeakLoadKw != null
                        ? Math.round(feederPeakLoadKw * 100) / 100
                        : undefined,
                    unit: "kW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Load",
                    value:
                      feederAvgLoadKw != null
                        ? Math.round(feederAvgLoadKw * 100) / 100
                        : undefined,
                    unit: "kW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Peak Temp",
                    value:
                      feederPeakTemp != null
                        ? Math.round(feederPeakTemp * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Temp",
                    value:
                      feederAvgTemp != null
                        ? Math.round(feederAvgTemp * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
            </>
          ) : selectedGroupBy.rowKey === "group_generation" ? (
            <>
              <MetricChip
                items={[
                  {
                    label: "Peak Generation",
                    value:
                      genMetrics?.peak_generation_kw != null
                        ? Math.round(genMetrics.peak_generation_kw * 100) / 100
                        : undefined,
                    unit: "kW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Generation",
                    value:
                      genMetrics?.avg_generation_kw != null
                        ? Math.round(genMetrics.avg_generation_kw * 100) / 100
                        : undefined,
                    unit: "kW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Peak Temp",
                    value:
                      genMetrics?.peak_temp_c != null
                        ? Math.round(genMetrics.peak_temp_c * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Temp",
                    value:
                      genMetrics?.avg_temp_c != null
                        ? Math.round(genMetrics.avg_temp_c * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
            </>
          ) : (
            <>
              <MetricChip
                items={[
                  {
                    label: `Peak ${groupLabelPrefix} Load`,
                    value:
                      groupPeakLoad != null
                        ? Math.round(groupPeakLoad * 100) / 100
                        : undefined,
                    unit: groupLoadUnit,
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: `Average ${groupLabelPrefix} Load`,
                    value:
                      groupAvgLoad != null
                        ? Math.round(groupAvgLoad * 100) / 100
                        : undefined,
                    unit: groupLoadUnit,
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: `Peak ${groupLabelPrefix} Temp`,
                    value:
                      groupPeakTemp != null
                        ? Math.round(groupPeakTemp * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: `Average ${groupLabelPrefix} Temp`,
                    value:
                      groupAvgTemp != null
                        ? Math.round(groupAvgTemp * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
            </>
          )}
        </div>
        <div className="gas-chart-container flex-1 min-h-0">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="flex flex-col justify-between border-gray-200 bg-white">
                <div
                  className="gas-heading"
                  style={{
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: "8px",
                  }}
                >
                  <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                    <h2 className="chartHeading shrink-0">
                      {selectedGroupBy.label}
                    </h2>
                    <div className="flex items-center gap-x-3">
                      <FilterTab
                        options={DataTabs.map((item) => item.label)}
                        selected={[dataTab.label]}
                        onClick={(_, index) =>
                          dispatch(setViewMode(DataTabs[index]))
                        }
                        variant="pill-outline"
                      />

                      {/* <DownloadButton
                        onClick={() =>
                          [
                            downloadChartTableCSV,
                            downloadSummaryCSV,
                            downloadChartTableCSV,
                          ][
                            ["Chart", "Summary", "Table"].indexOf(dataTab.label)
                          ]?.(tablePar)
                        }
                      /> */}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-x-3 min-w-0">
                    <JvvnlEntitySearchBar
                      show={{
                        circle: showCircleSearch,
                        feeder: showFeederSearch,
                        substation: showSubstationSearch,
                        discomSubstation: showDiscomSubstationSearch,
                        netConsumption: showNetConsumptionSearch,
                        generation: showGenerationSearch,
                      }}
                      hierSepClass={hierSepClass}
                      circleOptions={circleOptions}
                      selectedCircle={selectedCircle}
                      setSelectedCircle={setSelectedCircle}
                      selectedFeederTransco={selectedFeederTransco}
                      setSelectedFeederTransco={setSelectedFeederTransco}
                      setSelectedFeederSubstations={
                        setSelectedFeederSubstations
                      }
                      selectedFeederSubstations={selectedFeederSubstations}
                      feederSubstationOptions={feederSubstationOptions}
                      transcoOptionsForDiscom={transcoOptionsForDiscom}
                      filteredFeederOptions={filteredFeederOptions}
                      selectedFeederOptions={selectedFeederOptions}
                      setSelectedFeederOptions={setSelectedFeederOptions}
                      feederOpenSignal={feederOpenSignal}
                      substationOptions={substationOptions}
                      selectedSubstationOptions={selectedSubstationOptions}
                      setSelectedSubstationOptions={
                        setSelectedSubstationOptions
                      }
                      selectedTranscoForDiscom={selectedTranscoForDiscom}
                      setSelectedTranscoForDiscom={setSelectedTranscoForDiscom}
                      filteredDiscomSubstationOptions={
                        filteredDiscomSubstationOptions
                      }
                      selectedDiscomSubstations={selectedDiscomSubstations}
                      setSelectedDiscomSubstations={
                        setSelectedDiscomSubstations
                      }
                      netCircleOptions={netCircleOptions}
                      netCircle={netCircle}
                      setNetCircle={setNetCircle}
                      netTranscoOptions={netTranscoOptions}
                      netTransco={netTransco}
                      setNetTransco={setNetTransco}
                      netDiscomOptions={netDiscomOptions}
                      netDiscom={netDiscom}
                      setNetDiscom={setNetDiscom}
                      discomSubstations={discomSubstations}
                      genCircleOptions={circleOptions}
                      selectedGenCircle={selectedGenCircle}
                      setSelectedGenCircle={setSelectedGenCircle}
                      plantTypeOptions={plantTypeOptions}
                      selectedPlantTypeOptions={selectedPlantTypeOptions}
                      setSelectedPlantTypeOptions={setSelectedPlantTypeOptions}
                      plantOptions={plantOptions}
                      selectedPlantOptions={selectedPlantOptions}
                      setSelectedPlantOptions={setSelectedPlantOptions}
                      allPlants={allPlants}
                    />
                    <div className="flex items-center gap-x-2 shrink-0 self-center">
                      <BlockRangeSelector
                        blocks={Array.from({ length: 48 }, (_, i) => i + 1)}
                        placeholder="Search Block"
                        selectedOptions={selectedBlock.map((it) => it.value)}
                        setSelectedOptions={setSelectedBlock as any}
                        blockMinutes={30}
                      />
                      {selectedInterval.isTime && (
                        <>
                          <span className="text-gray-300 select-none ml-3 self-center">
                            {" "}
                            |
                          </span>
                          <CriticalPeriodsToggle
                            active={cpActive}
                            onToggle={setCpActive}
                            disabled={selectedBlock.length > 0}
                            disabledReason="Unavailable while specific blocks are selected. Clear block selection to view critical periods."
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="jvvnl-data-view flex-1 min-h-0 flex flex-col">
                {dataTab.label === "Chart" && (
                  <ReMixChart
                    tooltipBoundary="chart"
                    key={`jvvnl-${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}-${selectedFunction}`}
                    data={chartData}
                    showBrush={chartData.length > 0}
                    brushIndex={{
                      startIndex: 0,
                      endIndex: Math.max(0, chartData.length - 1),
                    }}
                    isTimeSlot={selectedInterval.isTime}
                    blockDurationMinutes={blockDurationMinutes}
                    onlyTitle
                    barCategoryGap={10}
                    unit={selectedUnit}
                    yAxisLabel={selectedUnit}
                    secondYAxisLabel={
                      selectedVariables.includes("Weather")
                        ? selectedWeatherMetricLabel
                        : undefined
                    }
                    legends={legends}
                    onLegendSelect={handleLegendSelect}
                    defaultSelectedLegends={selectedLegends}
                    xDataKey={xDataKey}
                    xTick={
                      selectedInterval.isTime && selectedBlock.length === 0
                        ? (v: any) =>
                            renderHourTick(
                              v,
                              10,
                              blockDurationMinutes === 30 ? 2 : 4,
                            )
                        : undefined
                    }
                    secondXDataKey={secondDataKey()}
                    showCriticalPeriods={selectedBlock.length === 0}
                    hideCriticalPeriodsButton={true}
                    activePeriodKinds={criticalPeriodKinds}
                    onActivePeriodKindsChange={setCriticalPeriodKinds}
                  />
                )}
                {dataTab.label === "Summary" && (
                  <LegendSummaryTable {...tablePar} disableCopy />
                )}
                {dataTab.label === "Table" && (
                  <ChartTable
                    {...tablePar}
                    hideCriticalPeriodsButton={true}
                    cpActive={cpActive}
                    onCpActiveChange={setCpActive}
                    disableCopy
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>Source:</p>}
              text="1. Weather : Open-Meteo&#10;2. Load : Discom&#10;3. Feeder : AMISP&#10;4. Generation : AMISP (HKRP)&#10;"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
